import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DestroyableComponentBase } from 'apps/idl/src/modules/utils/components/destroyable-component-base';
import { PlatformService } from 'apps/idl/src/services/security/platform.service';
import { CapacitorPluginScePlugin, StatusSCEPlugin } from 'capacitor-plugin-sce';
import { defer, EMPTY, from, iif, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, filter, switchMap, take, takeUntil, tap, timeout } from 'rxjs/operators';
import { SceEventService } from '../../../../sce4Mobile/sce-event.service';
import { SceFactory } from '../../../../sce4Mobile/sceFactory';
import { EDeviceType } from '../../../enums/EDeviceType';
import { DrawerPopoverService } from '../../../services/drawer-popover.service';
import { LecteurStorageService } from '../../../services/lecteurStorage.service';
import { LoaderService } from '../../../services/loader.service';
import { PanneauService } from '../../../services/panneau.service';
import { SnackbarService } from '../../../services/snackbar.service';

@Component({
	selector: 'di-panneau-connecter-lecteur',
	templateUrl: './panneau-connecter-lecteur.component.html',
	styleUrls: ['./panneau-connecter-lecteur.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class PanneauConnecterLecteurComponent extends DestroyableComponentBase implements OnInit, OnDestroy{

	public lecteurs: string[] = [];
	public selectedLecteur: string = "";
	public rechercheLecteurs = false;
	public connectionInProgress = false;

	constructor(
		private svcPanneau: PanneauService,
		private svcSceEvent : SceEventService,
		private svcLoader: LoaderService,
		private svcSnackbar: SnackbarService,        
		public cdr: ChangeDetectorRef,
		private svcPlatform: PlatformService,
		private svcLecteurStorage: LecteurStorageService,
		private svcDrawerPopover : DrawerPopoverService
	) {
		super();
	 }


	 public pluginSce : CapacitorPluginScePlugin;
	 

	public async ngOnInit() {

		const deviceTypeUser: EDeviceType = this.svcPlatform.getDeviceType();
		this.pluginSce = SceFactory.getSceInstance(deviceTypeUser)
		
		this.listenToSceStatus();
		const sceStatus = await this.pluginSce.getStatus()
		iif(() => !sceStatus.connected && !sceStatus.connecting,			
			defer(() => {
				this.svcLoader.showLoader("Connexion avec le SCE Mobile en cours ...");
				console.warn('[COMP] Connexion au SCE en cours...');
				return from(this.pluginSce.connect());
			}),
			defer(() => {
				this.retrySce();
				this.svcLoader.showLoader("Déconnexion du SCE Mobile en cours...");
				console.warn('[COMP] Déconnexion du SCE en cours...');
				return from(this.pluginSce.disconnect());
			})
		).pipe(
			takeUntil(this.destroyed$),
			catchError((err) => {
				this.svcLoader.hideLoader();
				console.error('[COMP] Erreur lors du connectToSCE initial', err);
				this.svcSceEvent.emitStatus('error');
				return EMPTY;
			})
		).subscribe();
	}

	public retrySce(): void {
		this.svcSceEvent.sceDisconnect$.pipe(
			take(1),
			takeUntil(this.destroyed$),
			tap(() => {
				this.svcLoader.showLoader("Connexion avec le SCE Mobile en cours ...")
				console.warn('[COMP] Connexion au SCE en cours...');
			}),
			concatMap(() => from(this.pluginSce.connect())),
			timeout(15000), 
			catchError(error => {
					this.svcLoader.hideLoader();
					this.svcSnackbar.showToast("error", "bottom center", "Erreur lors de la connexion au lecteur, veuillez réessayer.", null, null, null, "6000");
					this.svcDrawerPopover.close();
					this.svcPanneau.close();
					console.error('Timeout: aucune réponse après 15 secondes');
					return of(null); 
				}
			)
		).subscribe();
	}

	public override ngOnDestroy() {
		from(this.pluginSce.getStatus()).pipe(
			switchMap((status: StatusSCEPlugin) => {
			if (status.connected) {
				return from(this.pluginSce.disconnect());
			}
			return EMPTY;
			})
		).subscribe();
		super.ngOnDestroy();
	}

	private listenToSceStatus() {
		this.lecteurs = [];
		this.svcSceEvent.sceStatus$.pipe(
			takeUntil(this.destroyed$),
			filter(sceStatus => !!sceStatus),
			switchMap((sceStatus) => {
				switch (sceStatus.status) {
					case 'connected':
						console.warn('[COMP] Le SCE est prêt.');
						this.svcLoader.hideLoader();
						this.rechercheLecteurs = true;
						this.cdr.detectChanges();
						return from(this.pluginSce.searchDevices());
					case 'waiting-lecteur':
						this.rechercheLecteurs = true;
						this.cdr.detectChanges();
						return of(sceStatus);
					case 'error':
						console.warn('[COMP] Une erreur a été détectée dans le processus SCE.');
						this.svcSnackbar.showToast("error", "bottom center", `Erreur critique avec le lecteur : ${sceStatus.message ?? "Erreur Inconnue"}.`, null, null, null, "6000");
						return throwError(new Error(`Erreur SCE: ${sceStatus.message ?? "Erreur Inconnue"}`));
					case 'lecteur-detected':
						this.lecteurs.push(sceStatus.message);
						this.cdr.detectChanges();
						return of(sceStatus);
					case 'ready':
						this.rechercheLecteurs = false;
						this.cdr.detectChanges();
						if(this.selectedLecteur){
							this.svcSnackbar.showToast("success", "bottom center", `Lecteur ${this.selectedLecteur} connecté !`, null, null, null, "6000");
						}else{
							this.svcSnackbar.showToast("success", "bottom center", `Un lecteur est déjà connecté !`, null, null, null, "6000");
						}
						this.svcPanneau.close();
						return of(sceStatus);
					default: return of(sceStatus);
				}
			}),
			catchError(err => {
				console.error('[COMP] SCE status handling error:', err.message);
				this.svcSnackbar.showToast("error", "bottom center", `La connexion au lecteur SCE a échoué. Essayez de redémarrer le lecteur ou de relancer l'application.`, null, null, null, "6000");
				this.svcPanneau.close();
				this.svcLoader.hideLoader();
				return EMPTY;
			})
		).subscribe();
	}

	public onSelectLecteur(lecteur: string): void {
		if (this.connectionInProgress) return;
		if(this.selectedLecteur === lecteur) {
			this.selectedLecteur = "";
		}else{
			this.selectedLecteur = lecteur;
		}
		this.cdr.detectChanges();
	}

	public connectLecteur(): void {
		if(this.connectionInProgress) return;
		const deviceType: EDeviceType = this.svcPlatform.getDeviceType();
		this.svcLoader.showLoader(`Appairage Bluetooth en cours...`);
		timer(0, 10000).pipe(
			switchMap(() => from(this.pluginSce.connectToDevice({ deviceName: this.selectedLecteur })).pipe(
				tap(() => {
					this.svcLecteurStorage.setLecteurName(this.selectedLecteur)
					this.connectionInProgress = true;
				})
			)),
			takeUntil(this.destroyed$)
		).subscribe();
	}
}